import { Checkbox, FormSelect, Hint, LocaleEnum, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import debounce from 'lodash.debounce';
import SettingsLayout from 'modules/profile/components/settings-layout';
import { SettingsSection } from 'modules/profile/components/settings-section';
import { SettingsWrapper } from 'modules/profile/components/settings-wrapper';
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys';
import { NotificationSettingsTitleEnum } from 'modules/profile/notification-settings/enums/notification-settings-enum';
import { useNotificationSettings } from 'modules/profile/notification-settings/hooks/use-notification-settings';
import { NotificationSettingsInterface } from 'modules/profile/notification-settings/types/notification-settings-interface';
import { updatePaymentSettings } from 'modules/profile/payment/api/payment-settings-api';
import AgreementEditor from 'modules/profile/payment/components/agreement-editor';
import PaymentNotificationSettingsSkeleton from 'modules/profile/payment/components/payment-notification-settings-skeleton';
import PaymentSettingsSkeleton from 'modules/profile/payment/components/payment-settings-skeleton';
import { defaultErrors } from 'modules/profile/payment/constants/default-errors';
import { defaultValues } from 'modules/profile/payment/constants/default-values';
import { PaymentSettingsLoco } from 'modules/profile/payment/constants/payment-settings-loco';
import { usePaymentNotificationSettings } from 'modules/profile/payment/hooks/use-payment-notification-settings';
import { usePaymentSettings } from 'modules/profile/payment/hooks/use-payment-settings';
import { EditablePaymentSettings, PaymentSettingsErrorResponseContentInterface, PaymentSettingsErrorsInterface, UsePaymentSettingsInterface } from 'modules/profile/payment/types/payment-settings-interface';
import { groupPaymentNotificationSettings } from 'modules/profile/payment/utils/group-payment-notification-settings';
import { showSwitzerlandVatCheckbox } from 'modules/profile/payment/utils/show-switzerland-vat-checkbox';
import { getSubscriptionGivingAccessData } from 'modules/profile/payment/utils/subscription-utils';
import { useUserSettings } from 'modules/profile/user-settings/hooks/use-user-settings';
import { NextPage } from 'next';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import CheckboxRhf from 'shared/components/checkbox-rhf';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { useCurrencyData } from 'shared/hooks/use-currency-data';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { hasWorkspaces } from 'shared/hooks/use-user/utils/has-workspaces';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { getDomainHelpLink } from 'shared/utils/get-domain-help-link';
const setDefaultValues = (paymentSettingsData: UsePaymentSettingsInterface | undefined) => {
  if (!paymentSettingsData) {
    return defaultValues;
  }
  return {
    defaultCurrency: paymentSettingsData.defaultCurrency,
    doNotUseVat: paymentSettingsData.doNotUseVat,
    doNotUseVatForSwitzerland: paymentSettingsData.doNotUseVatForSwitzerland,
    salesInvoiceFooter: paymentSettingsData.salesInvoiceFooter,
    paymentPageAgreement: paymentSettingsData.paymentPageAgreement,
    subscriptionGivingAccess: paymentSettingsData.subscriptionGivingAccess
  };
};
const PaymentSettings: NextPage = () => {
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    isWorkspaceOwner
  } = useUser();
  const {
    paymentSettingsData,
    mutate
  } = usePaymentSettings(true);
  const userCountry = useMemo(() => {
    if (!user) return null;
    if (isWorkspaceOwner) {
      return user.country;
    }
    const selectedWorkspace = hasWorkspaces(user) ? user.workspaceMemberships.find(el => el.workspaceOwner.id === user.workspaceOwner) : undefined;
    return selectedWorkspace?.workspaceOwner.country ?? null;
  }, [isWorkspaceOwner, user]);
  const {
    data: notificationSettings,
    mutate: mutateNotificationSettings,
    isAllowed
  } = useNotificationSettings();
  const {
    updatePaymentNotification
  } = usePaymentNotificationSettings();
  const currencyData = useCurrencyData();
  const [tempState, setTempState] = useState<EditablePaymentSettings>(setDefaultValues(paymentSettingsData));
  const [errors, setErrors] = useState<PaymentSettingsErrorsInterface>(defaultErrors);
  const [isFetching, setIsFetching] = useState(false);
  const {
    setValue,
    reset,
    control,
    getValues
  } = useForm<NotificationSettingsInterface>();
  const groupedSettings = groupPaymentNotificationSettings(notificationSettings);
  useEffect(() => {
    setTempState(setDefaultValues(paymentSettingsData));
  }, [paymentSettingsData]);
  const onSubmit = async () => {
    const paymentPageAgreement = Object.entries<string>(tempState.paymentPageAgreement).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [key]: value
      };
    }, {} as Record<LocaleEnum, string>);
    try {
      const data: EditablePaymentSettings = {
        defaultCurrency: tempState.defaultCurrency,
        doNotUseVat: tempState.doNotUseVat,
        doNotUseVatForSwitzerland: tempState.doNotUseVatForSwitzerland,
        paymentPageAgreement: paymentPageAgreement,
        salesInvoiceFooter: tempState.salesInvoiceFooter,
        subscriptionGivingAccess: tempState.subscriptionGivingAccess
      };
      setIsFetching(true);
      const response = await updatePaymentSettings(data);
      await mutate(response.data, false);
      setIsFetching(false);
      toast.success(t('global.changes_saved'));
      setErrors(defaultErrors);
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        const paymentSettingErrors = e.errors as unknown as PaymentSettingsErrorResponseContentInterface;
        const agreementErrors = errors.paymentPageAgreement;
        if (paymentSettingErrors.fields.paymentPageAgreement) {
          Object.entries(paymentSettingErrors.fields.paymentPageAgreement).map(([locale, error]) => {
            agreementErrors[locale as LocaleEnum] = error.join('');
          });
        }
        const salesInvoiceFooterErrors = errors.salesInvoiceFooter;
        if (paymentSettingErrors.fields.paymentPageAgreement) {
          Object.entries(paymentSettingErrors.fields.paymentPageAgreement).map(([locale_0, error_0]) => {
            salesInvoiceFooterErrors[locale_0 as LocaleEnum] = error_0.join('');
          });
        }
        setErrors({
          defaultCurrency: paymentSettingErrors.fields.defaultCurrency?.join(''),
          doNotUseVat: paymentSettingErrors.fields.doNotUseVat?.join(''),
          doNotUseVatForSwitzerland: paymentSettingErrors.fields.doNotUseVatForSwitzerland?.join(''),
          paymentPageAgreement: agreementErrors,
          salesInvoiceFooter: salesInvoiceFooterErrors,
          subscriptionGivingAccess: paymentSettingErrors.fields.subscriptionGivingAccess?.join('')
        });
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  const handleSave = async () => {
    try {
      const {
        disableCompanyCustomerPurchase,
        disableIndividualCustomerPurchase
      } = getValues();
      setIsFetching(true);
      const response_0 = await updatePaymentNotification({
        disableCompanyCustomerPurchase: disableCompanyCustomerPurchase,
        disableIndividualCustomerPurchase: disableIndividualCustomerPurchase
      });
      await mutateNotificationSettings(data_0 => ({
        ...data_0,
        ...response_0
      }), false);
      setIsFetching(false);
      toast.success(t('dashboard.settings.notifications.changes_saved'));
    } catch (e_0) {
      setIsFetching(false);
    }
  };
  useEffect(() => {
    if (notificationSettings) {
      reset(notificationSettings);
    }
  }, [notificationSettings]);
  const debouncedHandleSave = debounce(handleSave, 1000);
  return <SettingsLayout titles={[{
    caption: t(settingsMenuLocoKeys.payment_page),
    isStatic: true
  }]} onSave={onSubmit} disabled={!paymentSettingsData} isFetching={isFetching}>
      {paymentSettingsData && user ? <SettingsWrapper>
          {isAllowed && <SettingsSection headingTitle={t('dashboard.settings.payment_page.receipt_settings_title')}>
            {groupedSettings ? Object.keys(groupedSettings).map(value_0 => {
          const notification = value_0 as keyof NotificationSettingsInterface;
          return <CheckboxRhf control={control} name={notification} setValue={setValue} key={notification} label={t(NotificationSettingsTitleEnum[notification])} afterChange={debouncedHandleSave} />;
        }) : <PaymentNotificationSettingsSkeleton />}
          </SettingsSection>}
          <SettingsSection headingTitle={t('dashboard.settings.payment_page.tax_title')}>
            <Checkbox value={tempState.doNotUseVat} label={t('dashboard.settings.payment_page.without_vat_title')} onChange={() => setTempState(prev => {
          return {
            ...prev,
            doNotUseVat: !prev.doNotUseVat
          };
        })} />
            {showSwitzerlandVatCheckbox(userCountry) && <Checkbox value={tempState.doNotUseVatForSwitzerland} label={<Hint label={t('dashboard.settings.payment_page.swiss_without_vat_description')}>
                    {t('dashboard.settings.payment_page.swiss_without_vat')}
                  </Hint>} onChange={() => setTempState(prev_0 => {
          return {
            ...prev_0,
            doNotUseVatForSwitzerland: !prev_0.doNotUseVatForSwitzerland
          };
        })} />}
          </SettingsSection>
          <SettingsSection headingTitle={t('dashboard.settings.payment_page.price_plan_title')}>
            <FormSelect onChange={value_1 => {
          setTempState(prev_1 => {
            return {
              ...prev_1,
              defaultCurrency: value_1
            };
          });
        }} value={tempState.defaultCurrency} data={currencyData} label={t('dashboard.settings.payment_page.default_currency_popover_title')} error={errors.defaultCurrency} withoutCloseIcon />
            <FormSelect onChange={value_2 => {
          setTempState(prev_2 => {
            return {
              ...prev_2,
              subscriptionGivingAccess: value_2
            };
          });
        }} value={tempState.subscriptionGivingAccess} data={getSubscriptionGivingAccessData(t)} label={<Hint label={<>
                      {t('dashboard.settings.payment_page.subscription_giving_access_tooltip')}
                      {'. '}
                      <LinkWithoutPrefetch target={'_blank'} rel={'noreferrer'} className={'primary-link'} href={getDomainHelpLink(user?.dashboardLocale)}>
                        {t('dashboard.settings.payment_page.click_here_to_learn_more')}
                      </LinkWithoutPrefetch>
                    </>}>
                  {t('dashboard.settings.payment_page.subscription_giving_access')}
                </Hint>} error={errors.subscriptionGivingAccess} withoutCloseIcon />
          </SettingsSection>
          <SettingsSection headingTitle={t('dashboard.settings.payment_page.terms_conditions_title')}>
            <AgreementEditor tempState={tempState} setTempState={setTempState} locale={user.dashboardLocale} errors={errors.paymentPageAgreement} />
          </SettingsSection>
        </SettingsWrapper> : <PaymentSettingsSkeleton />}
    </SettingsLayout>;
};
export const getServerSideProps = getBaseServerSideProps(PaymentSettingsLoco, 'global.settings');
export default PaymentSettings;